import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Footer, LanguageSwitcher } from "@/components/common";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Invite from "@/pages/Invite";
import Dashboard from "@/pages/Dashboard";
import SpacePage from "@/pages/SpacePage";
import Jurisdiction from "@/pages/Jurisdiction";
import News from "@/pages/News";
import Science from "@/pages/Science";
import DebateRoom from "@/pages/DebateRoom";
import PartyProfile from "@/pages/PartyProfile";
import Representatives from "@/pages/Representatives";
import Admin from "@/pages/Admin";
import Profile from "@/pages/Profile";
import Notifications from "@/pages/Notifications";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return (<div className="min-h-screen flex flex-col"><Navbar /><main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 py-8">{children}</main><Footer /></div>);
}

// Publicly viewable (read-only for guests): space pages + debate rooms reached via invite links.
function PublicView({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  return (
    <div className="min-h-screen flex flex-col">
      {user ? <Navbar /> : (
        <header className="border-b-2 border-foreground bg-background sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
            <Link to="/" className="font-display font-black text-2xl tracking-tight text-foreground">Deliber<span className="text-primary">.</span>Act</Link>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <Link to="/login" className="text-sm font-bold text-foreground px-3 py-2">Log in</Link>
              <Link to="/register" className="nb-btn bg-primary text-white text-sm px-4 py-2">Get started</Link>
            </div>
          </div>
        </header>
      )}
      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 py-8">{children}</main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/invite/:slug" element={<Invite />} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/space/:slug" element={<PublicView><SpacePage /></PublicView>} />
            <Route path="/jurisdiction/:level/:value" element={<Protected><Jurisdiction /></Protected>} />
            <Route path="/news" element={<Protected><News /></Protected>} />
            <Route path="/science" element={<Protected><Science /></Protected>} />
            <Route path="/debates/:id" element={<PublicView><DebateRoom /></PublicView>} />
            <Route path="/party/:id" element={<Protected><PartyProfile /></Protected>} />
            <Route path="/representatives" element={<Protected><Representatives /></Protected>} />
            <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
            <Route path="/admin" element={<Protected><Admin /></Protected>} />
            <Route path="/profile" element={<Protected><Profile /></Protected>} />
            <Route path="/profile/:id" element={<Protected><Profile /></Protected>} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
export default App;
